import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaRocket,
  FaChartLine,
  FaCode,
  FaPalette,
  FaArrowRight,
  FaCheckCircle
} from 'react-icons/fa';

const FeaturedServicesSection = () => {
  const services = [
    {
      icon: <FaRocket />,
      title: 'Strategic Planning',
      description: 'Comprehensive business strategies that drive growth and maximize ROI.',
      features: ['Market Analysis', 'Competitive Research', 'Growth Roadmap'],
      color: 'primary'
    },
    {
      icon: <FaChartLine />,
      title: 'Digital Marketing',
      description: 'Data-driven campaigns that increase visibility and generate quality leads.',
      features: ['SEO Optimization', 'Social Media', 'PPC Campaigns'],
      color: 'success'
    },
    {
      icon: <FaCode />,
      title: 'Web Development',
      description: 'Custom websites and applications built for performance and user experience.',
      features: ['Responsive Design', 'Fast Loading', 'SEO Ready'],
      color: 'info'
    },
    {
      icon: <FaPalette />,
      title: 'Brand Design',
      description: 'Creative visual identity that resonates with your target audience.',
      features: ['Logo Design', 'Brand Guidelines', 'Marketing Materials'],
      color: 'warning'
    }
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const item = {
    hidden: {
      y: 50,
      opacity: 0,
      scale: 0.9
    },
    show: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const cardHover = {
    y: -4,
    scale: 1.01,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  };

  const iconHover = {
    scale: 1.02,
    rotate: 3,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  };

  const buttonHover = {
    scale: 1.02,
    transition: {
      duration: 0.2,
      ease: "easeOut"
    }
  };

  return (
    <section className="py-5 position-relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      {/* Background Elements */}
      <div className="position-absolute top-0 start-0 w-100 h-100" style={{
        background: 'radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.05) 0%, transparent 50%)'
      }}></div>

      <div className="container position-relative">
        <motion.div
          className="text-center mb-5"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <motion.span
            className="badge bg-primary-subtle text-primary mb-3 px-3 py-2 rounded-pill"
            style={{ fontSize: '0.9rem', fontWeight: '500' }}
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Our Services
          </motion.span>
          <h2 className="fw-bold mb-4 gradient-text-animated">
            Comprehensive Digital Solutions
          </h2>
          <p className="lead text-muted mx-auto" style={{ maxWidth: '48rem', fontSize: '1.1rem' }}>
            We offer end-to-end digital services to help your business thrive in the modern marketplace.
          </p>
        </motion.div>

        <motion.div
          className="row g-3"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="col-12 col-sm-6 col-lg-3"
              variants={item}
              whileHover={cardHover}
            >
              <motion.div
                className="card h-100 border-0 position-relative overflow-hidden card-hover-effect d-flex flex-column"
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
                  borderRadius: '20px',
                }}
              >
                {/* Gradient Overlay */}
                <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                  background: `linear-gradient(135deg, ${service.color === 'primary' ? 'rgba(59, 130, 246, 0.02)' :
                    service.color === 'success' ? 'rgba(16, 185, 129, 0.02)' :
                      service.color === 'info' ? 'rgba(6, 182, 212, 0.02)' :
                        'rgba(245, 158, 11, 0.02)'} 0%, transparent 50%)`,
                  opacity: 0,
                  transition: 'opacity 0.3s ease'
                }}></div>

                <div className="card-body p-4 d-flex flex-column position-relative flex-grow-1">
                  <motion.div
                    className="text-start mb-4"
                    whileHover={iconHover}
                  >
                    <motion.div
                      className={`d-inline-flex align-items-center justify-content-center rounded-4 text-white position-relative service-icon-wrapper service-icon-float`}
                      style={{
                        width: 'clamp(50px, 8vw, 70px)',
                        height: 'clamp(50px, 8vw, 70px)',
                        background: service.color === 'primary' ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' :
                          service.color === 'success' ? 'linear-gradient(135deg, #10b981, #059669)' :
                            service.color === 'info' ? 'linear-gradient(135deg, #06b6d4, #0891b2)' :
                              'linear-gradient(135deg, #f59e0b, #d97706)',
                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                      }}
                    >
                      <span className="fs-3">{service.icon}</span>
                    </motion.div>
                  </motion.div>

                  <div className="text-start flex-grow-1 d-flex flex-column">
                    <h5 className="card-title fw-bold mb-3" style={{ fontSize: '1.25rem', color: '#1e293b' }}>
                      {service.title}
                    </h5>
                    <p className="card-text text-muted mb-4" style={{ lineHeight: '1.6', fontSize: '0.95rem' }}>
                      {service.description}
                    </p>

                    <ul className="list-unstyled text-start flex-grow-1 m-0">
                      {service.features.map((feature, idx) => (
                        <motion.li
                          key={idx}
                          className="mb-2 d-flex align-items-center feature-item"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: idx * 0.1 }}
                          viewport={{ once: true }}
                        >
                          <FaCheckCircle className="text-success me-2" style={{ fontSize: '0.9rem' }} />
                          <span style={{ fontSize: '0.9rem', color: '#64748b' }}>{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  <motion.div
                    className="text-start mt-auto"
                    whileHover={buttonHover}
                  >
                    <Link
                      to="/services"
                      className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill"
                      style={{
                        borderWidth: '2px',
                        fontWeight: '500',
                        fontSize: '0.9rem',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Learn More
                      <FaArrowRight style={{ fontSize: '0.8rem' }} />
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="text-center mt-5"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Link
            to="/services"
            className="btn btn-primary btn-lg px-5 py-3 rounded-pill d-inline-flex align-items-center gap-2 btn-pulse"
            style={{
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none',
              fontWeight: '600',
              fontSize: '1rem',
              boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.3s ease'
            }}
            whileHover={{
              scale: 1.05,
              boxShadow: '0 12px 35px rgba(59, 130, 246, 0.4)',
              transition: { duration: 0.3 }
            }}
          >
            View All Services
            <FaArrowRight />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedServicesSection;
